<template>
  <div>
    <DashPeriodFilter
      :period="filters.period"
      :from="filters.from"
      :to="filters.to"
      :applied="appliedPeriod"
      :loading="loading"
      @change="onFilterChange"
    />

    <div v-if="loading && !loaded" class="text-center py-6 text-muted">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0">Carregando...</p>
    </div>

    <div v-else-if="errored" class="text-center py-6 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-2">{{ errorMessage || "Não foi possível carregar os dados." }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetch">Tentar novamente</button>
    </div>

    <div v-else>
      <div class="row g-3 mb-4">
        <div class="col-6 col-xl-4" v-for="c in statCards" :key="c.key">
          <div class="big-card h-100">
            <span class="big-icon"><i :class="c.icon"></i></span>
            <div class="big-info">
              <span class="big-value">{{ c.format(c.value) }}</span>
              <span class="big-label">
                {{ c.label }}
                <i
                  v-if="c.tooltip"
                  class="fa-solid fa-circle-info ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="c.tooltip"
                ></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-12">
          <WidgetCard title="% de pacientes com consulta e com cartão (coorte de cadastro)">
            <ChartLine :data="paymentAndSession" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Cadastros de pacientes (mensal)">
            <ChartBar :data="registrationTimeline" :options="stackedOptions" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Cadastros diários de pacientes">
            <ChartLine :data="dailyPatientRegistrations" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Pacientes com consultas">
            <span class="badge bg-light text-dark border acc-badge">total acumulado</span>
            <ChartDoughnut :data="patientsWithAppointments" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Pacientes com conta Stripe">
            <span class="badge bg-light text-dark border acc-badge">total acumulado</span>
            <ChartDoughnut :data="patientsWithStripe" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Visualizações de perfil por pacientes (mensal)">
            <ChartLine :data="profileViewsMonthly" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Visualizações de perfil por pacientes (diário)">
            <ChartLine :data="profileViewsDaily" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Média de sessões por coorte de pacientes">
            <p class="text-muted small mb-2 px-2">
              Cada barra reúne os pacientes que se cadastraram naquele mês e
              mostra quantas consultas cada um fez, em média, desde então. Quem
              nunca agendou conta como zero.
              <i
                class="fa-solid fa-circle-info ms-1"
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
                data-bs-html="true"
                data-bs-custom-class="dash-tooltip-wide"
                :title="avgSessionsCohortTooltip"
              ></i>
            </p>
            <ChartBar :data="avgSessionsByCohort" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Tempo médio até 1ª sessão de pacientes (coorte)">
            <ChartBar :data="timeToFirstByCohort" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Top pacientes por consultas" :fixed="false">
            <DashRankList :items="topPatientsItems" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Top pacientes por gasto" :fixed="false">
            <DashRankList :items="topBySpendItems" />
          </WidgetCard>
        </div>

        <div class="col-12">
          <DashLtvBlock
            title="LTV — Pacientes"
            count-label="Pacientes"
            :count="data.ltv?.patients"
            :total="data.ltv?.total"
            :average="data.ltv?.average"
            :items="ltvItems(data.ltv)"
            note="Valor total gasto pelo paciente em consultas ao longo de todo o relacionamento com o Psiapp — vitalício, sem corte. O filtro só decide quem entra na lista (quem consultou no período)."
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  COLORS,
  moneyBR,
  numberBR,
  groupSeries,
  singleSeries,
  baseOptions,
  dateTicks,
} from "@/utils/dashboardCharts";
import { Tooltip } from "bootstrap";
import WidgetCard from "./WidgetCard.vue";
import DashPeriodFilter from "./DashPeriodFilter.vue";
import DashRankList from "./DashRankList.vue";
import DashLtvBlock from "./DashLtvBlock.vue";

const typeLabel = (t) =>
  t === "user" ? "Pacientes" : t === "psychologist" ? "Psicólogos" : t;

export default {
  name: "DashPatients",
  components: { WidgetCard, DashPeriodFilter, DashRankList, DashLtvBlock },
  data() {
    return {
      loading: false,
      loaded: false,
      errored: false,
      errorMessage: "",
      filters: { period: "last_12_months", from: "", to: "" },
      appliedPeriod: null,
      data: {},
      stackedOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ stacked: true, ticks: dateTicks }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
        },
      },
    };
  },
  computed: {
    statCards() {
      return [
        {
          key: "new_patients",
          label: "Novos pacientes no período",
          icon: "fa-solid fa-user-plus",
          value: this.data.new_patients,
          format: (v) => numberBR(v),
        },
        {
          key: "with_sessions",
          label: "Pacientes com sessão no período",
          icon: "fa-regular fa-clock",
          value: this.data.with_sessions?.patient,
          format: (v) => numberBR(v),
          tooltip: "Pessoas com pelo menos uma sessão no período selecionado.",
        },
        {
          key: "avg_days_until_first_session",
          label: "Dias até a 1ª consulta de pacientes",
          icon: "fa-solid fa-hourglass-half",
          value: this.data.avg_days_until_first_session,
          format: (v) => `${(Number(v) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} dias`,
        },
      ];
    },
    paymentAndSession() {
      const rows = this.data.monthly_payment_and_session_percentage || [];
      return {
        labels: rows.map((r) => r.year_month),
        datasets: [
          {
            label: "% com consulta",
            data: rows.map((r) => Number(r.users_with_appointments_perc)),
            borderColor: COLORS[0],
            backgroundColor: COLORS[0],
            fill: false,
          },
          {
            label: "% com cartão",
            data: rows.map((r) => Number(r.perc_users_with_customer_id)),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            fill: false,
          },
        ],
      };
    },
    registrationTimeline() {
      const rows = (this.data.registration_timeline || []).map((r) => ({
        ...r,
        type: typeLabel(r.type),
      }));
      return groupSeries(rows, "year_month", "type", "total");
    },
    dailyPatientRegistrations() {
      return singleSeries(this.data.daily_patient_registrations || [], "day", "total", "Pacientes", 1);
    },
    patientsWithAppointments() {
      const d = this.data.patients_with_appointments || {};
      return {
        labels: ["Com consultas", "Sem consultas"],
        datasets: [
          {
            data: [d.with_appointments || 0, d.without_appointments || 0],
            backgroundColor: [COLORS[3], COLORS[9]],
          },
        ],
      };
    },
    patientsWithStripe() {
      const d = this.data.patients_with_stripe || {};
      return {
        labels: ["Com Stripe", "Sem Stripe"],
        datasets: [
          {
            data: [d.with_stripe || 0, d.without_stripe || 0],
            backgroundColor: [COLORS[2], COLORS[9]],
          },
        ],
      };
    },
    profileViewsMonthly() {
      return singleSeries(this.data.profile_views_monthly || [], "year_month", "total_views", "Visualizações", 2);
    },
    profileViewsDaily() {
      return singleSeries(this.data.profile_views_daily || [], "day", "total_views", "Visualizações", 6);
    },
    avgSessionsByCohort() {
      return singleSeries(
        this.data.avg_sessions_per_user_by_cohort || [],
        "year_month",
        "avg_sessions",
        "Média de sessões",
        3
      );
    },
    avgSessionsCohortTooltip() {
      return [
        'Agrupamos os pacientes pelo mês em que se cadastraram e somamos todas as consultas pagas que eles fizeram desde aí — em qualquer data, não só no mês do cadastro. O total é dividido por todo mundo da turma, inclusive quem nunca agendou, e por isso a média costuma ficar abaixo de 1.',
        'Um valor de 0,5 pode significar metade da turma com uma consulta cada, ou poucos pacientes com muitas consultas e o resto sem nenhuma. Para separar os dois casos, veja o gráfico "% de pacientes com consulta e com cartão", que mostra quantos de fato agendaram.',
        'As barras mais recentes tendem a ser menores: quem se cadastrou este mês teve semanas para marcar consulta, quem se cadastrou há um ano teve doze meses. A queda no fim do gráfico é tempo de vida, não piora de conversão.',
      ].join("<br><br>");
    },
    timeToFirstByCohort() {
      return singleSeries(
        this.data.time_to_first_session_by_cohort || [],
        "year_month",
        "avg_days_until_first_appointment",
        "Dias até 1ª sessão",
        5
      );
    },
    topPatientsItems() {
      return this.withPerc(this.data.top_patients, "appointment_count").map((r) => ({
        id: r.id,
        name: r.name,
        subtitle: r.email,
        value: numberBR(r.appointment_count),
        unit: "consultas",
        perc: r.perc,
        linkTo: `${this.$paths.users.detail}/${r.id}`,
      }));
    },
    topBySpendItems() {
      // shape exato ainda não confirmado contra API real — assume {id,name,amount_spent}
      return this.withPerc(this.data.top_by_spend, "amount_spent").map((r) => ({
        id: r.id,
        name: r.name,
        value: moneyBR(r.amount_spent),
        perc: r.perc,
        linkTo: `${this.$paths.users.detail}/${r.id}`,
      }));
    },
  },
  mounted() {
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
    this.fetch();
  },
  methods: {
    moneyBR,
    numberBR,
    onFilterChange(f) {
      this.filters = f;
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      this.errored = false;
      try {
        const res = await this.$dashboardService.patients(this.filters);
        this.data = res.data || {};
        this.appliedPeriod = res.period || null;
        this.loaded = true;
      } catch (e) {
        this.errored = true;
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
    withPerc(list, key) {
      const rows = list || [];
      const max = rows.reduce((m, r) => Math.max(m, Number(r[key]) || 0), 0);
      return rows.map((r) => ({
        ...r,
        perc: max ? Math.round(((Number(r[key]) || 0) / max) * 100) : 0,
      }));
    },
    ltvItems(block) {
      return this.withPerc(block?.top, "ltv").map((r) => ({
        id: r.id,
        name: r.name,
        value: moneyBR(r.ltv),
        perc: r.perc,
        linkTo: `${this.$paths.users.detail}/${r.id}`,
      }));
    },
  },
};
</script>

<style scoped>
.big-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(18, 38, 63, 0.04);
}
.big-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex: 0 0 46px;
  background-color: rgba(251, 164, 134, 0.16);
  color: var(--bs-primary, #fba486);
}
.big-info {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  min-width: 0;
}
.big-value {
  font-size: 1.7rem;
  font-weight: 800;
  color: #12263f;
  letter-spacing: -0.5px;
}
.big-label {
  font-size: 0.78rem;
  color: #8896a6;
  margin-top: 2px;
}
.acc-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 0.65rem;
}
</style>
